import React, { useState, useEffect } from 'react';
import ReviewBlock from './ReviewBlock';
import QuestionsReviewBlock from './QuestionsReviewBlock';
import Tag from '@common/Tag';
import CircularLoader from '@common/CircularLoader';
import TextbookSectionCard from './TextbookSectionCard';
import ExerciseCard from './ExerciseCard';
import WalkthroughCard from './WalkthroughCard';
import InteractiveCard from './InteractiveCard';
import VideoCard from './VideoCard';
import WorksheetCard from './WorksheetCard';
import lessonResourceService from '../lessonResourceService';

const SectionReviewBlock = ({ sectionKey, conceptId, headerActions }) => {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [selections, setSelections] = useState({});

    useEffect(() => {
        if (!conceptId) return;

        setLoading(true);
        fetchResourcesForSection(sectionKey, conceptId)
            .then(fetchedItems => {
                setItems(fetchedItems);
                const initialSelections = {};
                fetchedItems.forEach(item => {
                    initialSelections[item.id] = item.topPick || false;
                });
                setSelections(initialSelections);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching resources:', error);
                setLoading(false);
            });
    }, [sectionKey, conceptId]);

    const toggleItem = (id) => setSelections(s => ({ ...s, [id]: !s[id] }));

    if (!conceptId) {
        return (
            <div className="ch-lesson-planner-section-empty">
                <p>Select a concept to view available resources</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="ch-lesson-planner-section-loading">
                <CircularLoader />
            </div>
        );
    }

    if (sectionKey === 'questions') {
        return <QuestionsReviewBlock conceptId={conceptId} headerActions={headerActions} />;
    }

    const group = getGroupConfig(sectionKey);
    if (!group || items.length === 0) {
        return (
            <div className="ch-lesson-planner-section-empty">
                <p>No {group?.label || 'resources'} available for this concept</p>
            </div>
        );
    }

    const topPicks = items.filter(i => i.topPick);
    const moreOptions = items.filter(i => !i.topPick);
    const groupSelected = items.filter(item => selections[item.id]).length;

    return (
        <ReviewBlock
            icon={group.icon}
            label={group.label}
            selectedCount={groupSelected}
            totalCount={items.length}
            headerActions={headerActions}
            moreOptionsContent={moreOptions.length > 0 && moreOptions.map(item =>
                renderItem(sectionKey, item, selections[item.id], toggleItem)
            )}
            moreOptionsCount={moreOptions.length}
        >
            {topPicks.length > 0 && <Tag className="ch-lesson-planner-ai-picks-tag">✦ AI TOP PICKS</Tag>}
            {topPicks.map(item => renderItem(sectionKey, item, selections[item.id], toggleItem))}
        </ReviewBlock>
    );
};

// Fetch resources from backend based on section type
async function fetchResourcesForSection(sectionKey, conceptId) {
    switch (sectionKey) {
        case 'textbookContent':
            return fetchTextbookContent(conceptId);
        case 'exercise':
            return fetchExercises(conceptId);
        case 'quiz':
            return fetchQuizzes(conceptId);
        case 'walkthroughs':
            return fetchWalkthroughs(conceptId);
        case 'extras':
            return fetchExtras(conceptId);
        case 'videos':
            return fetchVideos(conceptId);
        case 'worksheets':
            return fetchWorksheets(conceptId);
        default:
            return [];
    }
}

async function fetchTextbookContent(conceptId) {
    const pageInfo = await lessonResourceService.getTutorials(conceptId, 'LESSON', null, 0, 100);
    return pageInfo.content.map(tutorial => ({
        id: `tb-${tutorial.tutorialId}`,
        tutorialId: tutorial.tutorialId,
        title: tutorial.tutorialName,
        summary: tutorial.contentSummary || '',
        pagePill: tutorial.tutorialNumber ? `p. ${tutorial.tutorialNumber}` : '',
        topPick: false, // Could be determined by metadata
    }));
}

async function fetchExercises(conceptId) {
    const pageInfo = await lessonResourceService.getTutorials(conceptId, 'EXERCISE', null, 0, 100);
    return pageInfo.content.map(tutorial => ({
        id: `ex-${tutorial.tutorialId}`,
        tutorialId: tutorial.tutorialId,
        title: tutorial.tutorialName,
        chips: [], // Could add chips based on question types
        topPick: false,
    }));
}

async function fetchQuizzes(conceptId) {
    const pageInfo = await lessonResourceService.getQuestionClusters(conceptId, ['QUICK_QUIZ'], null, 0, 100);
    return pageInfo.content.map(cluster => ({
        id: `qz-${cluster.questionClusterId}`,
        questionClusterId: cluster.questionClusterId,
        title: cluster.name,
        chips: [{ label: 'Questions', count: cluster.questionCount, variant: 'fluency' }],
        topPick: false,
    }));
}

async function fetchWalkthroughs(conceptId) {
    const pageInfo = await lessonResourceService.getWalkthroughQuestions(conceptId, 0, 100);
    return pageInfo.content.map(question => ({
        id: `wt-${question.questionId}`,
        questionId: question.questionId,
        title: question.instructions || 'Walkthrough Question',
        problemPreview: question.instructions || '',
        stepTrack: '', // Could calculate from question data if available
        topPick: false,
    }));
}

async function fetchExtras(conceptId) {
    const items = [];

    // Fetch Desmos activities
    try {
        const pageInfo = await lessonResourceService.getMediaResources(conceptId, 'DESMOS', 0, 50);
        items.push(...pageInfo.content.map(media => ({
            id: `desmos-${media.mediaId}`,
            mediaId: media.mediaId,
            title: media.mediaName,
            typePill: 'Desmos',
            topPick: false,
        })));
    } catch (e) {
        console.error('Error fetching Desmos:', e);
    }

    // Fetch Widgets
    try {
        const pageInfo = await lessonResourceService.getMediaResources(conceptId, 'WIDGET', 0, 50);
        items.push(...pageInfo.content.map(media => ({
            id: `widget-${media.mediaId}`,
            mediaId: media.mediaId,
            title: media.mediaName,
            typePill: 'Widget',
            topPick: false,
        })));
    } catch (e) {
        console.error('Error fetching Widgets:', e);
    }

    // Fetch Scorchers
    try {
        const pageInfo = await lessonResourceService.getMediaResources(conceptId, 'SCORCHER', 0, 50);
        items.push(...pageInfo.content.map(media => ({
            id: `scorcher-${media.mediaId}`,
            mediaId: media.mediaId,
            title: media.mediaName,
            typePill: 'Scorcher',
            topPick: false,
        })));
    } catch (e) {
        console.error('Error fetching Scorchers:', e);
    }

    // Fetch Games
    try {
        const pageInfo = await lessonResourceService.getMediaResources(conceptId, 'GAME', 0, 50);
        items.push(...pageInfo.content.map(media => ({
            id: `game-${media.mediaId}`,
            mediaId: media.mediaId,
            title: media.mediaName,
            typePill: 'Game',
            topPick: false,
        })));
    } catch (e) {
        console.error('Error fetching Games:', e);
    }

    return items;
}

async function fetchVideos(conceptId) {
    const pageInfo = await lessonResourceService.getMediaResources(conceptId, 'VIDEO', 0, 100);
    return pageInfo.content.map(media => ({
        id: `v-${media.mediaId}`,
        mediaId: media.mediaId,
        title: media.mediaName,
        meta: media.mediaDescription || 'Video', // MediaBean has 'mediaDescription', not 'mediaDesc'
        durationPill: '', // Duration not available in MediaBean
        topPick: false,
    }));
}

async function fetchWorksheets(conceptId) {
    const pageInfo = await lessonResourceService.getMediaResources(conceptId, 'WORKSHEET', 0, 100);
    return pageInfo.content.map(media => ({
        id: `ws-${media.mediaId}`,
        mediaId: media.mediaId,
        title: media.mediaName,
        meta: media.mediaDescription || '', // MediaBean has 'mediaDescription', not 'mediaDesc'
        topPick: false,
    }));
}

// Get group configuration by section key
function getGroupConfig(sectionKey) {
    const configs = {
        textbookContent: { key: 'textbookContent', icon: '📖', label: 'Textbook Section Content' },
        exercise: { key: 'exercise', icon: '✏️', label: 'Exercise' },
        quiz: { key: 'quiz', icon: '📋', label: 'Quiz' },
        walkthroughs: { key: 'walkthroughs', icon: '🔍', label: 'Walkthroughs' },
        extras: { key: 'extras', icon: '🎮', label: 'Scorcher, Desmos and Games' },
        videos: { key: 'videos', icon: '🎬', label: 'Videos' },
        worksheets: { key: 'worksheets', icon: '📄', label: 'Worksheets' },
    };
    return configs[sectionKey];
}

// Render item based on section type
function renderItem(sectionKey, item, checked, onToggle) {
    switch (sectionKey) {
        case 'textbookContent':
            return (
                <TextbookSectionCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    selected={checked}
                    onToggle={onToggle}
                    summary={item.summary}
                    pagePill={item.pagePill}
                />
            );
        case 'exercise':
        case 'quiz':
            return (
                <ExerciseCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    selected={checked}
                    onToggle={onToggle}
                    chips={item.chips}
                />
            );
        case 'walkthroughs':
            return (
                <WalkthroughCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    selected={checked}
                    onToggle={onToggle}
                    problemPreview={item.problemPreview}
                    stepTrack={item.stepTrack}
                />
            );
        case 'extras':
            return (
                <InteractiveCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    selected={checked}
                    onToggle={onToggle}
                    typePill={item.typePill}
                />
            );
        case 'videos':
            return (
                <VideoCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    selected={checked}
                    onToggle={onToggle}
                    meta={item.meta}
                    durationPill={item.durationPill}
                />
            );
        case 'worksheets':
            return (
                <WorksheetCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    selected={checked}
                    onToggle={onToggle}
                    meta={item.meta}
                />
            );
        default:
            return null;
    }
}

export default SectionReviewBlock;
